import { useMemo } from 'react'
import { BarChartWrapper } from '../../charts/BarChart'
import { buildDrillRsql } from '../../drill'
import { formatRatePercent } from '../../format'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import type { ProgramRateComparisonResult } from '../../../../types/insights'
import { buildProgramRateRows } from './analyticsMappers'

const WIDGET_KEY_SELF = WIDGET_KEY.programRateComparison

const RATE_SERIES = [
  { key: 'baseRate', label: 'Base rate' },
  { key: 'upliftRate', label: 'Uplift rate' },
]

/**
 * IB-282 View 3, AC3.2 — "Program base vs. uplift rate" grouped bar. The ONE genuinely new backend
 * wiring this story adds (`program-rate-comparison`, `cartridge.api.ProgramRateInsightApi` behind
 * it) — the widget itself reuses `BarChartWrapper` as-is (already multi-series/grouped), per the
 * Tech Lead design §2's "no new chart component for AC3.2" call.
 *
 * Two distinct "no data" states, never conflated (`ProgramRateComparisonResult.selected`): the
 * caller hasn't chosen a jurisdiction+fiscal year yet, vs. a real "0 programs" result for the
 * jurisdiction chosen. A program with no uplift authored renders base-rate-only (`analyticsMappers
 * .buildProgramRateRows`'s `NaN` sentinel — `formatRatePercent` renders it as "No uplift", never a
 * fabricated `0` bar) — the count is also surfaced as an explicit note below the chart, mirroring
 * `CreditValueByJurisdictionFyWidget`'s own "N excluded" disclosure pattern.
 */
export function ProgramRateCompareWidget({ filter, skipRefetch, onCrossFilter, onDrill }: WidgetProps) {
  const { status, data, message, retry } = useWidgetData<ProgramRateComparisonResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const rows = useMemo(() => buildProgramRateRows(data?.rows ?? []), [data])
  const noUpliftCount = rows.filter((r) => !r.hasUplift).length

  const notSelected = status === 'ok' && data?.selected === false
  const frameState = notSelected || (status === 'ok' && rows.length === 0) ? 'empty' : status
  const emptyMessage = notSelected
    ? 'Select a jurisdiction and fiscal year to compare program rates.'
    : 'No programs configured for this jurisdiction.'

  return (
    <WidgetFrame
      title="Program base vs. uplift rate"
      icon="bar-chart"
      state={frameState}
      message={frameState === 'empty' ? emptyMessage : message}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
    >
      <BarChartWrapper
        title="Program base vs. uplift rate"
        data={rows}
        categoryKey="code"
        categoryLabel="Program"
        series={RATE_SERIES}
        valueFormatter={formatRatePercent}
        onBarClick={(row) => onCrossFilter('programId', row.programId)}
      />
      {noUpliftCount > 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          {noUpliftCount} program{noUpliftCount === 1 ? '' : 's'} with no uplift rate authored — shown base-rate-only.
        </p>
      ) : null}
    </WidgetFrame>
  )
}
